/**
 * MusicVideoMode - Cinematic music video camera sequence
 * "Fly Me to the Moon" style animated clip
 *
 * NOT institutional - artistic, dynamic, emotional
 * Tells the story of humanity reaching for the stars
 */

import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

// Global shot index for overlay
let globalShotIndex = 0;
let globalTotalShots = 0;
export function getClipShotInfo() {
  return { shotIndex: globalShotIndex, totalShots: globalTotalShots };
}

// Scene positions (must match App.tsx)
const POSITIONS = {
  sun: new THREE.Vector3(-30, 0, 0),
  mercury: new THREE.Vector3(-2, 0, 15),
  earth: new THREE.Vector3(50, 0, 0),
  moon: new THREE.Vector3(50, 0, -5),
  eahp: new THREE.Vector3(-22, 0, 0),
  geoReceiver: new THREE.Vector3(54, 3, 5),
};

// Music video sequence - each shot is a "scene" in the story
// Total duration ~165 seconds (2:45) to match typical song length
interface Shot {
  name: string;
  duration: number; // seconds
  startPos: THREE.Vector3;
  endPos: THREE.Vector3;
  startTarget: THREE.Vector3;
  endTarget: THREE.Vector3;
  startFov?: number;
  endFov?: number;
  easing?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'dramatic';
  speedMult?: number; // For slow-mo effect on animations
  shake?: number; // Camera shake intensity
}

const MUSIC_VIDEO_SEQUENCE: Shot[] = [
  // === ACT 1: EARTH DEPARTURE ===
  // Moon at (50, 0, -5), Earth at (50, 0, 0), Ship starts at (50, 1.5, 0.3)
  // ORDER: 1, 3, 2 (Moon foreground, POV inside, then rocket rising)
  {
    name: 'MOON FOREGROUND - Earth and rocket behind',
    duration: 4,
    startPos: new THREE.Vector3(49.5, 0.8, -7),
    endPos: new THREE.Vector3(50, 1.2, -6.5),
    startTarget: new THREE.Vector3(50, 2, 0.5),
    endTarget: new THREE.Vector3(50, 4, 1.5),
    startFov: 55,
    endFov: 52,
    easing: 'easeOut',
  },
  {
    name: 'ENGINE FIRE - Raptor inferno below!',
    duration: 4,
    // Camera BELOW the rocket looking UP at engine fire - dramatic liftoff!
    startPos: new THREE.Vector3(50, 0.5, 1.5),
    endPos: new THREE.Vector3(50, 2, 2.5),
    startTarget: new THREE.Vector3(50, 3, 0.5),    // Looking up at engines
    endTarget: new THREE.Vector3(50, 8, 1),        // Following rocket ascent
    startFov: 90,
    endFov: 75,
    easing: 'easeIn',
    shake: 0.12,  // Heavy shake from engine roar
  },
  {
    name: 'ROCKET RISING BEHIND MOON - Epic scale',
    duration: 4,
    startPos: new THREE.Vector3(50.5, 0.5, -6),
    endPos: new THREE.Vector3(51, 1.5, -5),
    startTarget: new THREE.Vector3(50, 5, 2),
    endTarget: new THREE.Vector3(50, 8, 3),
    startFov: 52,
    endFov: 58,
    easing: 'linear',
  },
  {
    name: 'WIDE EARTH DEPARTURE - Ship leaving with GEO visible',
    duration: 5,
    // WIDE shot - ship visible leaving Earth, GEO + Moon in frame
    // Ship at t=12-17 is around (48, 15, 8) → (44, 13, 9)
    startPos: new THREE.Vector3(52, 10, 3),
    endPos: new THREE.Vector3(50, 18, 12),
    startTarget: new THREE.Vector3(48, 12, 6),   // Track the ship!
    endTarget: new THREE.Vector3(44, 13, 10),    // Following ship departure
    startFov: 65,
    endFov: 60,
    easing: 'easeInOut',
  },
  {
    name: 'COCKPIT INTERIOR - Elon & Ham photos visible',
    duration: 4,
    // Interior cockpit shot - photos of Elon Musk and Ham the chimp on dashboard
    // Camera inside ship looking at control panel then out window
    startPos: new THREE.Vector3(43, 12.5, 11.5),
    endPos: new THREE.Vector3(38, 11, 12),
    startTarget: new THREE.Vector3(42, 12.3, 11.8),  // Close dashboard with photos
    endTarget: new THREE.Vector3(15, 8, 14),          // Pan to stars ahead
    startFov: 65,
    endFov: 60,
    easing: 'easeInOut',
    shake: 0.002,
  },

  // === ACT 2: JOURNEY - Ship WITH environment ===
  {
    name: 'WIDE TRANSIT - Ship tiny in vast space',
    duration: 5,
    // VERY WIDE - ship small, whole solar system visible
    startPos: new THREE.Vector3(30, 40, 35),
    endPos: new THREE.Vector3(15, 35, 30),
    startTarget: new THREE.Vector3(20, 8, 12),   // Ship area but very wide
    endTarget: new THREE.Vector3(5, 5, 14),
    startFov: 75,
    endFov: 70,
    easing: 'linear',
  },
  {
    name: 'SHIP WITH SUN BACKDROP - Silhouette shot',
    duration: 4,
    // Ship silhouetted against distant sun/swarm
    startPos: new THREE.Vector3(12, 12, 20),
    endPos: new THREE.Vector3(8, 10, 18),
    startTarget: new THREE.Vector3(-30, 0, 0),  // Looking at SUN with ship passing
    endTarget: new THREE.Vector3(-25, 2, 5),
    startFov: 65,
    endFov: 60,
    easing: 'linear',
  },
  {
    name: 'SHIP NEARING MERCURY - Planet in view',
    duration: 4,
    // Mercury visible ahead, ship approaching
    startPos: new THREE.Vector3(6, 10, 20),
    endPos: new THREE.Vector3(2, 8, 18),
    startTarget: new THREE.Vector3(-2, 0, 15),  // Mercury
    endTarget: new THREE.Vector3(-2, 2, 15),
    startFov: 55,
    endFov: 50,
    easing: 'easeOut',
  },

  // === CAMERA PASSES SHIP - GOES TO SUN! ===
  {
    name: 'CAMERA OVERTAKES - Racing past SpaceX toward sun',
    duration: 3,
    // Camera PASSES the ship, heading toward sun
    startPos: new THREE.Vector3(0, 8, 17),
    endPos: new THREE.Vector3(-15, 6, 10),
    startTarget: new THREE.Vector3(-30, 0, 0),   // SUN!
    endTarget: new THREE.Vector3(-30, 0, 0),
    startFov: 60,
    endFov: 70,
    easing: 'easeIn',
    shake: 0.01,
  },
  {
    name: 'SUN ORBIT FAST 1 - Giant arc around star',
    duration: 4,
    // FAST giant orbit around the sun
    startPos: new THREE.Vector3(-15, 6, 10),
    endPos: new THREE.Vector3(-35, 15, -10),
    startTarget: new THREE.Vector3(-30, 0, 0),
    endTarget: new THREE.Vector3(-30, 0, 0),
    startFov: 75,
    endFov: 80,
    easing: 'linear',
    shake: 0.015,
  },
  {
    name: 'SUN ORBIT FAST 2 - Completing the arc',
    duration: 4,
    startPos: new THREE.Vector3(-35, 15, -10),
    endPos: new THREE.Vector3(-25, 8, 12),
    startTarget: new THREE.Vector3(-30, 0, 0),
    endTarget: new THREE.Vector3(-24, 1, 5.6),   // EAHP base coming into view
    startFov: 80,
    endFov: 65,
    easing: 'linear',
    shake: 0.01,
  },
  {
    name: 'EAHP HORIZONTAL ORBIT - Small tight circle',
    duration: 5,
    // Small HORIZONTAL orbit around EAHP construction base
    // EAHP at (-24.4, 1, 5.6)
    startPos: new THREE.Vector3(-20, 4, 8),
    endPos: new THREE.Vector3(-26, 3, 3),
    startTarget: new THREE.Vector3(-24.4, 1, 5.6),
    endTarget: new THREE.Vector3(-24.4, 1, 5.6),
    startFov: 50,
    endFov: 45,
    easing: 'easeInOut',
  },
  {
    name: 'MINING SHIP SPOTTED - Cargo heading to Mercury!',
    duration: 4,
    // Camera finds a mining ship departing EAHP toward Mercury
    // Mining route: EAHP (-24, 1, 5.6) → Mercury (-2, 0, 15)
    startPos: new THREE.Vector3(-26, 4, 4),
    endPos: new THREE.Vector3(-22, 3, 7),
    startTarget: new THREE.Vector3(-22, 2, 7),    // Ship leaving EAHP
    endTarget: new THREE.Vector3(-18, 2, 9),      // Following toward Mercury
    startFov: 50,
    endFov: 55,
    easing: 'easeOut',
  },
  {
    name: 'MINING CHASE - Racing alongside cargo ship!',
    duration: 4,
    // Chase shot - camera flies alongside mining ship toward Mercury
    startPos: new THREE.Vector3(-20, 3, 8),
    endPos: new THREE.Vector3(-12, 3, 12),
    startTarget: new THREE.Vector3(-18, 2, 9),    // Ship position
    endTarget: new THREE.Vector3(-8, 2, 14),      // Ship approaching Mercury
    startFov: 55,
    endFov: 50,
    easing: 'linear',
    shake: 0.01,
  },
  {
    name: 'MINING CHASE 1 - Following cargo ship',
    duration: 4,
    // Chasing mining ship toward Mercury
    startPos: new THREE.Vector3(-15, 5, 11),
    endPos: new THREE.Vector3(-8, 4, 14),
    startTarget: new THREE.Vector3(-12, 3, 13),
    endTarget: new THREE.Vector3(-5, 2, 15),
    startFov: 55,
    endFov: 50,
    easing: 'linear',
    shake: 0.008,
  },
  // === ACT 3: MERCURY OPERATIONS ===
  // Mercury center at (-2, 0, 15), radius 1.8
  // Cena 19 MUST start at 66s - removed MINING CHASE 2 and LASER BEAM FOCUS (-8s)

  // MERCURY RELAY HUB - Quick detail shot
  {
    name: 'MERCURY RELAY DETAIL - Optical station close-up',
    duration: 4,
    // Quick close-up of the Mercury Optical Relay Hub - don't linger
    // Camera positioned OUTSIDE Mercury, looking at relay hub on sun-facing side
    startPos: new THREE.Vector3(-6.5, 2, 11),
    endPos: new THREE.Vector3(-5.5, 1.5, 12),
    startTarget: new THREE.Vector3(-5, 0.5, 13.35),  // Relay hub position
    endTarget: new THREE.Vector3(-5, 0.8, 13.4),
    startFov: 40,
    endFov: 35,
    easing: 'easeInOut',
  },
  // Direct to landing - no duplicate find/descent scenes
  {
    name: 'STARSHIP LANDING - Full ship descending to pad',
    duration: 6,
    // SpaceX landing on Mercury - single combined landing shot
    startPos: new THREE.Vector3(4, 8, 20),
    endPos: new THREE.Vector3(2, 4, 18.5),
    startTarget: new THREE.Vector3(-1.64, 8, 15.27),  // Ship descending
    endTarget: new THREE.Vector3(-1.64, 2.55, 15.27), // Landing pad
    startFov: 50,
    endFov: 40,
    easing: 'easeOut',
    speedMult: 0.8,
  },
  {
    name: 'LANDED - Ship on Mercury surface',
    duration: 3,
    startPos: new THREE.Vector3(2, 4, 18.5),
    endPos: new THREE.Vector3(1, 3.5, 18),
    startTarget: new THREE.Vector3(-1.64, 2.55, 15.27),
    endTarget: new THREE.Vector3(-1.64, 3, 15.27),
    startFov: 40,
    endFov: 42,
    easing: 'easeInOut',
  },
  {
    name: 'STARSHIP LIFTOFF - Rising from Mercury',
    duration: 5,
    startPos: new THREE.Vector3(1, 3.5, 18.5),
    endPos: new THREE.Vector3(3, 5, 20),
    startTarget: new THREE.Vector3(-1.64, 2.55, 15.27),
    endTarget: new THREE.Vector3(0, 12, 14),
    startFov: 42,
    endFov: 55,
    easing: 'easeIn',
    shake: 0.02,
  },
  // === ACT 4: RETURN JOURNEY - SpaceX heading home! ===
  // HeroStarship liftoff ends at 96s, ReturnStarship appears from 96s onwards
  // User request: Follow ship → pass it → fast laser sweep → GEO orbit (close→far) →
  //               show laser to Earth → Earth infrastructure → sideways to see ship landing

  // Camera leaves Mercury, chasing along laser path (ship lifts off behind us)
  {
    name: 'LASER DEPARTURE - Racing away from Mercury',
    duration: 4,
    // Camera races away from Mercury along laser path toward Earth
    // HeroStarship is lifting off behind us
    startPos: new THREE.Vector3(0, 8, 16),
    endPos: new THREE.Vector3(8, 6, 14),
    startTarget: new THREE.Vector3(20, 4, 10),   // Looking toward Earth
    endTarget: new THREE.Vector3(30, 5, 8),
    startFov: 70,
    endFov: 75,
    easing: 'easeIn',
    shake: 0.015,
  },
  {
    name: 'LASER SWEEP 1 - Fast along beam network',
    duration: 4,
    // FAST camera racing along laser beam toward Earth!
    // Mercury relay behind us, heading toward GEO receiver
    startPos: new THREE.Vector3(12, 5, 12),
    endPos: new THREE.Vector3(28, 4, 8),
    startTarget: new THREE.Vector3(30, 4, 7),
    endTarget: new THREE.Vector3(45, 3, 5),
    startFov: 80,
    endFov: 85,
    easing: 'linear',
    shake: 0.02,
  },
  {
    name: 'LASER SWEEP 2 - Arriving at GEO receiver',
    duration: 4,
    // Continue fast along laser, GEO receiver coming into view
    // GEO receiver at (54, 3, 5)
    startPos: new THREE.Vector3(32, 4, 7),
    endPos: new THREE.Vector3(48, 5, 6),
    startTarget: new THREE.Vector3(50, 3, 5),
    endTarget: POSITIONS.geoReceiver,
    startFov: 85,
    endFov: 65,
    easing: 'easeOut',
    shake: 0.012,
  },
  {
    name: 'GEO FAST ORBIT - Quick spin around receiver station!',
    duration: 6,
    // FAST dramatic orbit around GEO receiver - one rapid 180° sweep
    startPos: new THREE.Vector3(55, 4, 6),
    endPos: new THREE.Vector3(53, 6, 3),
    startTarget: POSITIONS.geoReceiver,
    endTarget: POSITIONS.geoReceiver,
    startFov: 42,
    endFov: 50,
    easing: 'easeInOut',
    shake: 0.005,
  },
  {
    name: 'LASER TO EARTH - Racing down the beam!',
    duration: 5,
    // Camera FOLLOWS the laser beam FROM GEO station TO Earth!
    // Fast tracking shot along the energy path
    startPos: new THREE.Vector3(53, 5, 4),
    endPos: new THREE.Vector3(50.5, 3, 1),
    startTarget: new THREE.Vector3(52, 3, 3),    // Along laser path
    endTarget: POSITIONS.earth,                   // Earth surface
    startFov: 55,
    endFov: 45,
    easing: 'easeIn',
    shake: 0.01,
  },
  {
    name: 'EARTH ARRIVAL - Ground station receiving power',
    duration: 3,
    // Quick shot of Earth receiving the beam, then pan to find ship
    startPos: new THREE.Vector3(50.5, 3, 1),
    endPos: new THREE.Vector3(51, 4, 2),
    startTarget: POSITIONS.earth,
    endTarget: new THREE.Vector3(50, 2, 0.5),    // Looking toward landing area
    startFov: 45,
    endFov: 50,
    easing: 'easeOut',
  },
  {
    name: 'SIDEWAYS TO SHIP - Finding returning SpaceX!',
    duration: 4,
    // Camera looks SIDEWAYS and finds the returning SpaceX approaching
    // Ship at t=106: (36, 10, 3) → t=110: (42, 9, 2.5)
    startPos: new THREE.Vector3(51, 5, 2),
    endPos: new THREE.Vector3(44, 11, 5),
    startTarget: new THREE.Vector3(50, 2, 0.5),   // Start on Earth/landing area
    endTarget: new THREE.Vector3(42, 9, 2.5),     // Match RETURN_WAYPOINTS t=110
    startFov: 50,
    endFov: 55,
    easing: 'easeInOut',
  },
  {
    name: 'RETURN TRACKING - Following SpaceX home',
    duration: 5,
    // Track the returning ship as it approaches Earth
    // Ship at t=110: (42, 9, 2.5) → t=115: (46, 7, 1.5)
    startPos: new THREE.Vector3(44, 11, 6),
    endPos: new THREE.Vector3(48, 9, 4),
    startTarget: new THREE.Vector3(42, 9, 2.5),   // Match RETURN_WAYPOINTS t=110
    endTarget: new THREE.Vector3(46, 7, 1.5),     // Match RETURN_WAYPOINTS t=115
    startFov: 55,
    endFov: 50,
    easing: 'linear',
    shake: 0.008,
  },
  {
    name: 'EARTH ENTRY - Retrograde flip maneuver!',
    duration: 5,
    // Ship performing the iconic flip for Earth landing
    // Ship at t=115: (46, 7, 1.5) → t=120: (50, 4, 0.8)
    startPos: new THREE.Vector3(48, 9, 4),
    endPos: new THREE.Vector3(51, 6, 2),
    startTarget: new THREE.Vector3(46, 7, 1.5),    // Match RETURN_WAYPOINTS t=115
    endTarget: new THREE.Vector3(50, 4, 0.8),      // Match RETURN_WAYPOINTS t=120
    startFov: 50,
    endFov: 45,
    easing: 'easeInOut',
    speedMult: 0.8,
  },
  {
    name: 'GRAND LANDING - SpaceX touching down from behind!',
    duration: 6,
    // THE iconic SpaceX landing - camera BEHIND ship watching it descend!
    // Ship at t=120: (50, 4, 0.8) → t=126: (50, 1.5, 0.3)
    startPos: new THREE.Vector3(50, 6, 3),        // Behind and above
    endPos: new THREE.Vector3(50, 3, 2),          // Following down from behind
    startTarget: new THREE.Vector3(50, 4, 0.8),   // Match RETURN_WAYPOINTS t=120
    endTarget: new THREE.Vector3(50, 1.5, 0.3),   // Match RETURN_WAYPOINTS t=126
    startFov: 50,
    endFov: 40,
    easing: 'easeOut',
    speedMult: 0.7,
    shake: 0.02,
  },
  {
    name: 'LANDING COMPLETE - Celebrating touchdown',
    duration: 4,
    // Ship landed - camera orbits around celebrating the landing
    // Ship at (50, 1.5, 0.3)
    startPos: new THREE.Vector3(50, 3, 2),
    endPos: new THREE.Vector3(48, 3, 1),
    startTarget: new THREE.Vector3(50, 1.5, 0.3),
    endTarget: new THREE.Vector3(50, 2, 0.3),
    startFov: 40,
    endFov: 45,
    easing: 'easeInOut',
  },
  {
    name: 'FINAL VISTA - Moon and landed SpaceX',
    duration: 6,
    // Pull back to show Moon in distance, Earth, and the landed Starship
    // Moon at (50, 0, -5), Ship at (50, 1.5, 0.3)
    startPos: new THREE.Vector3(48, 3, 1),
    endPos: new THREE.Vector3(52, 6, -3),
    startTarget: new THREE.Vector3(50, 2, 0.3),
    endTarget: new THREE.Vector3(50, 1, -1),      // Between ship and Moon
    startFov: 45,
    endFov: 65,
    easing: 'dramatic',
  },
  {
    name: 'CREDITS - Earth-Moon system',
    duration: 8,
    // Final pullback showing the whole system - mission complete
    startPos: new THREE.Vector3(52, 5, -4),
    endPos: new THREE.Vector3(55, 15, 10),
    startTarget: new THREE.Vector3(50, 1, -1.5),
    endTarget: new THREE.Vector3(50, 0, -2),
    startFov: 65,
    endFov: 55,
    easing: 'easeOut',
  },
];

// Easing functions
const easings = {
  linear: (t: number) => t,
  easeIn: (t: number) => t * t * t,
  easeOut: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInOut: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  dramatic: (t: number) => {
    // Slow start, fast middle, slow end - very cinematic
    if (t < 0.2) return t * t * 12.5; // 0 to 0.5
    if (t > 0.8) return 1 - Math.pow(1 - t, 2) * 2.5; // 0.5 to 1
    return 0.5 + (t - 0.5) * 0.833; // linear middle
  },
};

// Track clip time globally for HeroStarship sync
let globalClipTime = 0;
let globalClipActive = false;

// Pre-allocated vectors for camera (PERFORMANCE)
const _cameraPos = new THREE.Vector3();
const _cameraTarget = new THREE.Vector3();

export function getClipTime() {
  return { time: globalClipTime, active: globalClipActive };
}

export function MusicVideoMode() {
  const { camera } = useThree();
  const timeRef = useRef(0);
  const currentShotRef = useRef(0);
  const shotTimeRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentShotNameRef = useRef('');

  const {
    isClipMode,
    stopClipMode,
    updateClipProgress,
    setAnimationSpeed
  } = useStore();

  // Calculate total duration
  const totalDuration = MUSIC_VIDEO_SEQUENCE.reduce((sum, shot) => sum + shot.duration, 0);

  // Reset when activated and start music
  useEffect(() => {
    if (isClipMode) {
      timeRef.current = 0;
      currentShotRef.current = 0;
      shotTimeRef.current = 0;
      globalShotIndex = 0;
      globalTotalShots = MUSIC_VIDEO_SEQUENCE.length;
      updateClipProgress(0, MUSIC_VIDEO_SEQUENCE[0].name);

      // Try to play music
      try {
        audioRef.current = new Audio('/fly-me-to-the-moon.mp3');
        audioRef.current.volume = 0.7;
        audioRef.current.play().catch(() => {
          console.log('Music file not found - playing without audio');
        });
      } catch (e) {
        console.log('Could not load audio');
      }
    } else {
      // Stop music when clip mode ends
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setAnimationSpeed(1);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isClipMode]);

  useFrame((state, delta) => {
    if (!isClipMode) {
      globalClipActive = false;
      return;
    }

    // Update time
    timeRef.current += delta;
    shotTimeRef.current += delta;
    globalClipTime = timeRef.current;
    globalClipActive = true;

    // Find current shot
    let accumulatedTime = 0;
    let shotIndex = 0;
    for (let i = 0; i < MUSIC_VIDEO_SEQUENCE.length; i++) {
      if (timeRef.current < accumulatedTime + MUSIC_VIDEO_SEQUENCE[i].duration) {
        shotIndex = i;
        break;
      }
      accumulatedTime += MUSIC_VIDEO_SEQUENCE[i].duration;
      if (i === MUSIC_VIDEO_SEQUENCE.length - 1) {
        // Video complete
        stopClipMode();
        return;
      }
    }

    // Update shot if changed
    if (shotIndex !== currentShotRef.current) {
      currentShotRef.current = shotIndex;
      shotTimeRef.current = 0;
      globalShotIndex = shotIndex;
    }

    const shot = MUSIC_VIDEO_SEQUENCE[shotIndex];
    currentShotNameRef.current = shot.name;
    const shotProgress = Math.min(shotTimeRef.current / shot.duration, 1);
    const easedProgress = easings[shot.easing || 'easeInOut'](shotProgress);

    // Update overall progress in store
    const overallProgress = (timeRef.current / totalDuration) * 100;
    updateClipProgress(overallProgress, shot.name);

    // Animation speed multiplier for slow-mo shots
    if (shot.speedMult) {
      setAnimationSpeed(shot.speedMult);
    } else {
      setAnimationSpeed(1);
    }

    // Interpolate camera position (using pre-allocated vector)
    _cameraPos.lerpVectors(shot.startPos, shot.endPos, easedProgress);

    // Add camera shake if specified
    if (shot.shake) {
      _cameraPos.x += (Math.random() - 0.5) * shot.shake;
      _cameraPos.y += (Math.random() - 0.5) * shot.shake;
      _cameraPos.z += (Math.random() - 0.5) * shot.shake;
    }

    // Interpolate target (using pre-allocated vector)
    _cameraTarget.lerpVectors(shot.startTarget, shot.endTarget, easedProgress);

    // Interpolate FOV
    if (shot.startFov && shot.endFov && camera instanceof THREE.PerspectiveCamera) {
      camera.fov = THREE.MathUtils.lerp(shot.startFov, shot.endFov, easedProgress);
      camera.updateProjectionMatrix();
    }

    // Apply camera transform
    camera.position.copy(_cameraPos);
    camera.lookAt(_cameraTarget);
  });

  return null;
}

// ============================================================
// HERO STARSHIP - Syncs with clip for Mercury landing sequence
// ============================================================
// UPDATED Shot timing (cumulative) - ALL SHOTS TRACK THE PROTAGONIST:
// 0-4 MOON FOREGROUND | 4-8 ROCKET RISING | 8-13 POV INSIDE | 13-18 ORBIT AROUND
// 18-22 GEO RECEIVER | 22-25 SHIP DEPARTURE | 25-30 CABIN CRUISE | 30-34 WINDOW VIEW
// 34-39 TRANSIT BEGIN | 39-43 CHASE SHOT | 43-48 FLYBY WITH MINING | 48-53 SOLAR SYSTEM CROSSING
// 53-57 SWARM APPROACH | 57-61 PASSING EAHP | 61-67 FINAL TRANSIT | 67-72 MERCURY ARRIVAL
// 72-77 SPACEX FINAL APPROACH | 77-82 POLAR BASE VIEW | 82-87 STARSHIP LANDING
// 87-91 POLAR BASE | 91-96 STARSHIP LIFTOFF

// Starship appears during STARSHIP LANDING and stays through LIFTOFF
// UPDATED TIMING: Removed LASER BEAM FOCUS and MINING CHASE 2 (-8s)
// ACT 3 timing: RELAY_DETAIL (62-66), LANDING (66-72), LANDED (72-75), LIFTOFF (75-80)
const HERO_SHIP_START = 66;      // Start of STARSHIP LANDING (1:06)
const HERO_APPROACH_END = 69;    // Midway through landing (flip)
const HERO_LANDING_END = 72;     // End of STARSHIP LANDING
const HERO_LANDED_END = 75;      // End of LANDED
const HERO_LIFTOFF_END = 80;     // End of LIFTOFF
const HERO_SEPARATION_TIME = 81.5; // Hot staging moment

// Pre-allocated vectors (PERFORMANCE - no garbage collection)
const _mercuryPos = new THREE.Vector3(-2, 0, 15);
// EXACT POLAR BASE POSITION - calculated from IndustrialMercury:
// Mercury at (-2, 0, 15), radius 1.8
// polarBasePosition = [radius * 0.2, radius * 0.98, radius * 0.15] = [0.36, 1.764, 0.27]
// World position: (-2 + 0.36, 0 + 1.764, 15 + 0.27) = (-1.64, 1.764, 15.27)
const _polarBasePos = new THREE.Vector3(-1.64, 1.764, 15.27);
// Starship path: Start FAR during MERCURY APPROACH, then descend during POLAR BASE VIEW
const _approachStart = new THREE.Vector3(15, 25, 30); // Far away - visible during MERCURY APPROACH
const _approachMid = new THREE.Vector3(6, 15, 22);    // Getting closer
const _landingStart = new THREE.Vector3(-1.64, 8, 15.27);   // Above the X
const _landingEnd = new THREE.Vector3(-1.64, 2.55, 15.27);  // ON THE X! (base Y 1.764 + pad 0.04 + ship offset ~0.75)
const _liftoffEnd = new THREE.Vector3(0, 12, 13);
const _departureDir = new THREE.Vector3(0.6, 0.7, -0.4).normalize();
const _tempPos = new THREE.Vector3();
const _tempDir = new THREE.Vector3();
const _defaultUp = new THREE.Vector3(0, 1, 0);
const _targetQuat = new THREE.Quaternion();

// Separated booster tracking
const _boosterPos = new THREE.Vector3();
const _boosterVel = new THREE.Vector3();

export function HeroStarship() {
  const groupRef = useRef<THREE.Group>(null);
  const engineGlowRef = useRef<THREE.PointLight>(null);
  const engineConesRef = useRef<THREE.Group>(null);
  const boosterRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (!groupRef.current) return;

    const { time, active } = getClipTime();
    if (!active || time < HERO_SHIP_START || time > HERO_LIFTOFF_END + 8) {
      groupRef.current.visible = false;
      if (boosterRef.current) boosterRef.current.visible = false;
      return;
    }

    groupRef.current.visible = true;

    let flipProgress = 0;
    let engineIntensity = 0.3; // Much lighter default
    let legsDeployed = 0;

    if (time < HERO_APPROACH_END) {
      // APPROACHING - bezier curve toward base
      const duration = HERO_APPROACH_END - HERO_SHIP_START;
      const t = Math.max(0, (time - HERO_SHIP_START) / duration);
      const eased = t * t * (3 - 2 * t); // smoothstep
      const t1 = 1 - eased;

      // Quadratic bezier: start -> mid -> end
      _tempPos.set(
        t1 * t1 * _approachStart.x + 2 * t1 * eased * _approachMid.x + eased * eased * _landingStart.x,
        t1 * t1 * _approachStart.y + 2 * t1 * eased * _approachMid.y + eased * eased * _landingStart.y,
        t1 * t1 * _approachStart.z + 2 * t1 * eased * _approachMid.z + eased * eased * _landingStart.z
      );
      engineIntensity = 0.2 + Math.sin(time * 3) * 0.1; // Gentle cruise

    } else if (time < HERO_LANDING_END) {
      // LANDING - flip maneuver and descent
      const t = (time - HERO_APPROACH_END) / (HERO_LANDING_END - HERO_APPROACH_END);
      flipProgress = Math.min(t * 1.8, 1); // Flip happens in first 55% of landing
      legsDeployed = Math.max(0, (t - 0.3) / 0.7); // Legs deploy after flip starts

      _tempPos.lerpVectors(_landingStart, _landingEnd, t * t); // Ease into landing

      // Retro burn - subtle orange glow
      engineIntensity = 0.8 + Math.sin(time * 15) * 0.2;

    } else if (time < HERO_LANDED_END) {
      // LANDED - sitting on pad
      _tempPos.copy(_landingEnd);
      flipProgress = 1;
      legsDeployed = 1;
      engineIntensity = 0; // Engines off

    } else if (time < HERO_LIFTOFF_END) {
      // LIFTOFF
      const t = (time - HERO_LANDED_END) / (HERO_LIFTOFF_END - HERO_LANDED_END);
      const eased = t * t; // Accelerating lift
      _tempPos.lerpVectors(_landingEnd, _liftoffEnd, eased);
      flipProgress = 1 - t * 0.3; // Slight tilt as it lifts
      legsDeployed = Math.max(0, 1 - t * 2); // Legs retract quickly

      // Liftoff burn - builds up
      engineIntensity = 1.2 + t * 0.8 + Math.sin(time * 12) * 0.15;

    } else {
      // DEPARTURE - continue upward
      const t = (time - HERO_LIFTOFF_END);
      _tempPos.copy(_liftoffEnd).addScaledVector(_departureDir, t * t * 3);
      engineIntensity = 0.6 + Math.sin(time * 8) * 0.1;

      // HOT STAGING SEPARATION
      if (boosterRef.current) {
        if (time >= HERO_SEPARATION_TIME) {
          boosterRef.current.visible = true;
          const sepT = time - HERO_SEPARATION_TIME;
          // Booster falls back toward Mercury
          _boosterPos.copy(_liftoffEnd).addScaledVector(_departureDir, (HERO_SEPARATION_TIME - HERO_LIFTOFF_END) * 2);
          _boosterPos.y -= sepT * sepT * 2; // Gravity pull
          _boosterPos.x += sepT * 0.5; // Drift
          boosterRef.current.position.copy(_boosterPos);
          boosterRef.current.rotation.x += 0.02; // Tumble
          boosterRef.current.rotation.z += 0.015;
        } else {
          boosterRef.current.visible = false;
        }
      }
    }

    groupRef.current.position.copy(_tempPos);

    // Orientation - point away from Mercury (engines toward surface during landing)
    _tempDir.subVectors(_tempPos, _mercuryPos).normalize();
    if (flipProgress >= 0.5 && time < HERO_LIFTOFF_END) {
      // After flip: engines toward Mercury (retrograde for landing)
    } else if (time < HERO_LANDING_END && flipProgress < 0.5) {
      _tempDir.negate(); // Before flip: nose toward Mercury
    } else if (time >= HERO_LIFTOFF_END) {
      // During departure: nose forward (away from Mercury)
      _tempDir.copy(_departureDir);
    }

    _targetQuat.setFromUnitVectors(_defaultUp, _tempDir);
    groupRef.current.quaternion.slerp(_targetQuat, 0.12);

    // Engine glow - MUCH LIGHTER
    if (engineGlowRef.current) {
      engineGlowRef.current.intensity = engineIntensity;
      // Color based on phase: blue cruise, orange retro, blue liftoff
      const color = (time >= HERO_APPROACH_END && time < HERO_LANDED_END) ? 0xff8844 : 0x88aaff;
      engineGlowRef.current.color.setHex(color);
    }

    // Engine cones visibility and opacity
    if (engineConesRef.current) {
      engineConesRef.current.visible = engineIntensity > 0.1;
      engineConesRef.current.children.forEach((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
          child.material.opacity = Math.min(0.5, engineIntensity * 0.3);
        }
      });
    }
  });

  return (
    <>
      <group ref={groupRef} visible={false} scale={0.3}>
        <group>
          {/* === STARSHIP UPPER STAGE === */}

          {/* Main hull - stainless steel */}
          <mesh>
            <cylinderGeometry args={[0.45, 0.45, 3.2, 16]} />
            <meshStandardMaterial color={0xf0f0f0} metalness={0.9} roughness={0.1} />
          </mesh>

          {/* Nose cone */}
          <mesh position={[0, 2.1, 0]}>
            <coneGeometry args={[0.45, 1.2, 16]} />
            <meshStandardMaterial color={0xeaeaea} metalness={0.85} roughness={0.15} />
          </mesh>

          {/* Heat shield tiles (dark side) */}
          <mesh position={[0, 0.5, -0.42]} rotation={[0.1, 0, 0]}>
            <boxGeometry args={[0.65, 2.6, 0.02]} />
            <meshStandardMaterial color={0x151515} roughness={0.95} />
          </mesh>

          {/* === SPACEX LOGO === */}
          {/* Logo panel - black rectangle with white text area */}
          <group position={[0.44, 0.6, 0]} rotation={[0, Math.PI / 2, 0]}>
            {/* Black background panel */}
            <mesh position={[0, 0, 0]}>
              <planeGeometry args={[0.7, 0.12]} />
              <meshStandardMaterial color={0x000000} roughness={0.8} />
            </mesh>
            {/* "SPACE" letters - white blocks */}
            {[-0.25, -0.15, -0.05, 0.05, 0.15].map((x, i) => (
              <mesh key={`s-${i}`} position={[x, 0.01, 0.001]}>
                <boxGeometry args={[0.06, 0.06, 0.002]} />
                <meshBasicMaterial color={0xffffff} />
              </mesh>
            ))}
            {/* "X" - larger stylized X */}
            <mesh position={[0.28, 0.01, 0.001]} rotation={[0, 0, Math.PI / 4]}>
              <boxGeometry args={[0.08, 0.02, 0.002]} />
              <meshBasicMaterial color={0xffffff} />
            </mesh>
            <mesh position={[0.28, 0.01, 0.001]} rotation={[0, 0, -Math.PI / 4]}>
              <boxGeometry args={[0.08, 0.02, 0.002]} />
              <meshBasicMaterial color={0xffffff} />
            </mesh>
          </group>

          {/* SpaceX logo on opposite side too */}
          <group position={[-0.44, 0.6, 0]} rotation={[0, -Math.PI / 2, 0]}>
            <mesh position={[0, 0, 0]}>
              <planeGeometry args={[0.7, 0.12]} />
              <meshStandardMaterial color={0x000000} roughness={0.8} />
            </mesh>
            {[-0.25, -0.15, -0.05, 0.05, 0.15].map((x, i) => (
              <mesh key={`s2-${i}`} position={[x, 0.01, 0.001]}>
                <boxGeometry args={[0.06, 0.06, 0.002]} />
                <meshBasicMaterial color={0xffffff} />
              </mesh>
            ))}
            <mesh position={[0.28, 0.01, 0.001]} rotation={[0, 0, Math.PI / 4]}>
              <boxGeometry args={[0.08, 0.02, 0.002]} />
              <meshBasicMaterial color={0xffffff} />
            </mesh>
            <mesh position={[0.28, 0.01, 0.001]} rotation={[0, 0, -Math.PI / 4]}>
              <boxGeometry args={[0.08, 0.02, 0.002]} />
              <meshBasicMaterial color={0xffffff} />
            </mesh>
          </group>

          {/* Forward flaps */}
          {[Math.PI/2, -Math.PI/2].map((rotY, i) => (
            <mesh key={i} position={[Math.cos(rotY) * 0.5, 1.7, Math.sin(rotY) * 0.5]} rotation={[0, rotY, 0.12]}>
              <boxGeometry args={[0.02, 0.45, 0.3]} />
              <meshStandardMaterial color={0x1a1a1a} roughness={0.85} />
            </mesh>
          ))}

          {/* Aft flaps */}
          {[Math.PI/2, -Math.PI/2].map((rotY, i) => (
            <mesh key={`aft-${i}`} position={[Math.cos(rotY) * 0.5, -0.9, Math.sin(rotY) * 0.5]} rotation={[0, rotY, 0.1]}>
              <boxGeometry args={[0.02, 0.8, 0.45]} />
              <meshStandardMaterial color={0x1a1a1a} roughness={0.85} />
            </mesh>
          ))}

          {/* Engine section */}
          <mesh position={[0, -1.8, 0]}>
            <cylinderGeometry args={[0.4, 0.5, 0.35, 16]} />
            <meshStandardMaterial color={0x666666} metalness={0.8} roughness={0.3} />
          </mesh>

          {/* Engine bells */}
          {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
            <mesh key={`bell-${i}`} position={[Math.cos(angle) * 0.18, -2.05, Math.sin(angle) * 0.18]} rotation={[Math.PI, 0, 0]}>
              <coneGeometry args={[0.1, 0.2, 12, 1, true]} />
              <meshStandardMaterial color={0x444444} metalness={0.9} roughness={0.2} side={THREE.DoubleSide} />
            </mesh>
          ))}

          {/* Engine glow cones - DRAMATIC for landing/liftoff */}
          <group ref={engineConesRef}>
            {/* Main engine flames - 3 Raptor engines */}
            {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
              <group key={i} position={[Math.cos(angle) * 0.18, -2.1, Math.sin(angle) * 0.18]}>
                {/* Outer flame cone */}
                <mesh>
                  <coneGeometry args={[0.12, 0.8, 12]} />
                  <meshBasicMaterial
                    color={0xff6622}
                    transparent
                    opacity={0.7}
                    blending={THREE.AdditiveBlending}
                  />
                </mesh>
                {/* Inner hot core */}
                <mesh position={[0, -0.15, 0]}>
                  <coneGeometry args={[0.06, 0.5, 8]} />
                  <meshBasicMaterial
                    color={0xffffaa}
                    transparent
                    opacity={0.9}
                    blending={THREE.AdditiveBlending}
                  />
                </mesh>
                {/* Mach diamonds effect */}
                <mesh position={[0, -0.4, 0]}>
                  <sphereGeometry args={[0.04, 8, 8]} />
                  <meshBasicMaterial
                    color={0xffffff}
                    transparent
                    opacity={0.8}
                    blending={THREE.AdditiveBlending}
                  />
                </mesh>
              </group>
            ))}
            {/* Central plume glow */}
            <mesh position={[0, -2.6, 0]}>
              <coneGeometry args={[0.25, 1.2, 12]} />
              <meshBasicMaterial
                color={0xff8844}
                transparent
                opacity={0.5}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
          </group>

          {/* Engine light - BRIGHT for cinematic effect */}
          <pointLight
            ref={engineGlowRef}
            position={[0, -2.5, 0]}
            color={0xff8844}
            intensity={3}
            distance={15}
          />
          {/* Secondary fill light */}
          <pointLight
            position={[0, -3, 0]}
            color={0xffaa66}
            intensity={2}
            distance={10}
          />

          {/* Landing legs */}
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const angle = (i * Math.PI * 2) / 6;
            return (
              <group key={i} position={[Math.cos(angle) * 0.4, -1.65, Math.sin(angle) * 0.4]} rotation={[0.45, angle, 0]}>
                <mesh position={[0, -0.35, 0]}>
                  <boxGeometry args={[0.035, 0.75, 0.05]} />
                  <meshStandardMaterial color={0x444444} metalness={0.85} roughness={0.25} />
                </mesh>
                <mesh position={[0, -0.75, 0]}>
                  <cylinderGeometry args={[0.1, 0.12, 0.03, 8]} />
                  <meshStandardMaterial color={0x333333} metalness={0.7} roughness={0.4} />
                </mesh>
              </group>
            );
          })}
        </group>
      </group>

      {/* SEPARATED BOOSTER (after hot staging) */}
      <group ref={boosterRef} visible={false} scale={0.25}>
        {/* Simplified booster ring */}
        <mesh>
          <cylinderGeometry args={[0.5, 0.55, 1.2, 12]} />
          <meshStandardMaterial color={0xcccccc} metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Separation motors flash */}
        <mesh position={[0, 0.7, 0]}>
          <sphereGeometry args={[0.3, 8, 8]} />
          <meshBasicMaterial
            color={0xffaa44}
            transparent
            opacity={0.6}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      </group>
    </>
  );
}

// ============================================================
// RETURN STARSHIP - Travels from Mercury back to Earth
// ============================================================
// Visible from 84s (after Mercury liftoff) to end of clip
// Path: Mercury departure → transit → Earth approach → landing

// UPDATED TIMING: ACT 4 starts at 84s now (relay detail reduced to 4s)
// ACT 4 shot timing (84s start):
// LASER DEPARTURE (84-88), LASER SWEEP 1 (88-92), LASER SWEEP 2 (92-96),
// GEO ORBIT CLOSE (96-101), GEO ORBIT MEDIUM (101-106), GEO ORBIT FAR (106-111),
// LASER TO EARTH (111-115), EARTH INFRASTRUCTURE (115-119), SIDEWAYS TO SHIP (119-123),
// RETURN TRACKING (123-128), EARTH ENTRY (128-133), GRAND LANDING (133-139),
// LANDING COMPLETE (139-143), FINAL VISTA (143-149), CREDITS (149-157)
// FINAL TIMING: Cena 19 at 66s, GEO condensed (-9s more)
const RETURN_START = 80;           // After HeroStarship liftoff (80s)
const RETURN_LANDING_START = 120;  // Start of GRAND LANDING shot
const RETURN_LANDED = 126;         // End of GRAND LANDING shot

// Return journey waypoints - Mercury to Earth
// Ship starts at liftoff position and travels toward Earth
// Earth at (50, 0, 0) with radius 1.2 - ship must stay ABOVE Y=1.5 minimum
// Camera finds ship at ~106-110s (SIDEWAYS TO SHIP shot)
const RETURN_WAYPOINTS = [
  { t: 80,   pos: new THREE.Vector3(0, 15, 12) },     // Just lifted off from Mercury
  { t: 84,   pos: new THREE.Vector3(5, 18, 10) },     // Climbing away
  { t: 88,   pos: new THREE.Vector3(12, 16, 8) },     // Accelerating toward Earth
  { t: 94,   pos: new THREE.Vector3(22, 14, 6) },     // Mid transit (camera on laser/GEO)
  { t: 102,  pos: new THREE.Vector3(32, 12, 4) },     // Approaching Earth region
  { t: 106,  pos: new THREE.Vector3(36, 10, 3) },     // Camera starts looking for ship!
  { t: 110,  pos: new THREE.Vector3(42, 9, 2.5) },    // Camera finds ship (SIDEWAYS TO SHIP)
  { t: 115,  pos: new THREE.Vector3(46, 7, 1.5) },    // End of RETURN TRACKING
  { t: 120,  pos: new THREE.Vector3(50, 4, 0.8) },    // GRAND LANDING starts - flip position
  { t: 126,  pos: new THREE.Vector3(50, 1.5, 0.3) },  // LANDED on Earth surface
  { t: 200,  pos: new THREE.Vector3(50, 1.5, 0.3) },  // Stay landed for credits
];

// Pre-allocated vectors for return ship
const _returnPos = new THREE.Vector3();
const _returnDir = new THREE.Vector3();
const _returnPrevPos = new THREE.Vector3();
const _returnQuat = new THREE.Quaternion();
const _returnUp = new THREE.Vector3(0, 1, 0);
const _earthPos = new THREE.Vector3(50, 0, 0);

// Get position along return journey
function getReturnPosition(time: number, out: THREE.Vector3) {
  let i = 0;
  for (let j = 0; j < RETURN_WAYPOINTS.length - 1; j++) {
    if (time >= RETURN_WAYPOINTS[j].t && time < RETURN_WAYPOINTS[j + 1].t) {
      i = j;
      break;
    }
  }
  if (time >= RETURN_WAYPOINTS[RETURN_WAYPOINTS.length - 1].t) {
    i = RETURN_WAYPOINTS.length - 2;
  }

  const segStart = RETURN_WAYPOINTS[i].t;
  const segEnd = RETURN_WAYPOINTS[i + 1].t;
  const segT = Math.max(0, Math.min(1, (time - segStart) / (segEnd - segStart)));

  const p0 = RETURN_WAYPOINTS[Math.max(0, i - 1)].pos;
  const p1 = RETURN_WAYPOINTS[i].pos;
  const p2 = RETURN_WAYPOINTS[i + 1].pos;
  const p3 = RETURN_WAYPOINTS[Math.min(RETURN_WAYPOINTS.length - 1, i + 2)].pos;

  // Catmull-Rom interpolation
  const t2 = segT * segT;
  const t3 = t2 * segT;
  out.x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * segT + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
  out.y = 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * segT + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
  out.z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * segT + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3);
}

export function ReturnStarship() {
  const groupRef = useRef<THREE.Group>(null);
  const exhaustRef = useRef<THREE.Group>(null);
  const engineGlowRef = useRef<THREE.PointLight>(null);
  const initializedRef = useRef(false);
  const flipProgressRef = useRef(0);

  useFrame(() => {
    if (!groupRef.current) return;

    const { time, active } = getClipTime();

    // Visible during return journey (96s onwards)
    if (!active || time < RETURN_START) {
      groupRef.current.visible = false;
      initializedRef.current = false;
      return;
    }

    groupRef.current.visible = true;

    // Get current position
    getReturnPosition(time, _returnPos);

    // Initialize orientation
    if (!initializedRef.current) {
      groupRef.current.position.copy(_returnPos);
      _returnPrevPos.copy(_returnPos);
      initializedRef.current = true;
      return;
    }

    _returnPrevPos.copy(groupRef.current.position);
    groupRef.current.position.copy(_returnPos);

    // Engine intensity and flip progress
    let engineIntensity = 0;
    let legsDeployed = 0;

    if (time < RETURN_LANDING_START) {
      // Transit - engines on
      engineIntensity = 1.5 + Math.sin(time * 10) * 0.3;
      flipProgressRef.current = 0;

      // Point in direction of travel
      _returnDir.subVectors(_returnPos, _returnPrevPos);
      if (_returnDir.lengthSq() > 0.00001) {
        _returnDir.normalize();
        _returnQuat.setFromUnitVectors(_returnUp, _returnDir);
        groupRef.current.quaternion.slerp(_returnQuat, 0.08);
      }
    } else if (time < RETURN_LANDED) {
      // Landing sequence - flip and descend
      const landingT = (time - RETURN_LANDING_START) / (RETURN_LANDED - RETURN_LANDING_START);
      flipProgressRef.current = Math.min(landingT * 2, 1);
      legsDeployed = Math.max(0, (landingT - 0.3) / 0.7);
      engineIntensity = 2.5 + Math.sin(time * 15) * 0.4; // Heavy retro burn

      // Orient for landing - engines toward Earth
      _returnDir.subVectors(_returnPos, _earthPos).normalize();
      if (flipProgressRef.current > 0.5) {
        // After flip: engines toward Earth
      } else {
        _returnDir.negate(); // Before flip: nose toward Earth
      }
      _returnQuat.setFromUnitVectors(_returnUp, _returnDir);
      groupRef.current.quaternion.slerp(_returnQuat, 0.1);
    } else {
      // Landed
      flipProgressRef.current = 1;
      legsDeployed = 1;
      engineIntensity = 0;

      // Stand upright on Earth
      _returnDir.set(0, 1, 0);
      _returnQuat.setFromUnitVectors(_returnUp, _returnDir);
      groupRef.current.quaternion.slerp(_returnQuat, 0.15);
    }

    // Update engine glow
    if (engineGlowRef.current) {
      engineGlowRef.current.intensity = engineIntensity * 4;
      const color = time >= RETURN_LANDING_START && time < RETURN_LANDED ? 0xff6622 : 0x88aaff;
      engineGlowRef.current.color.setHex(color);
    }

    // Update exhaust visibility
    if (exhaustRef.current) {
      exhaustRef.current.visible = engineIntensity > 0.1;
      const scale = 0.5 + engineIntensity * 0.4;
      exhaustRef.current.scale.set(scale, scale * 1.5, scale);
    }
  });

  return (
    <group ref={groupRef} visible={false} scale={0.25}>
      {/* Starship body - stainless steel */}
      <mesh>
        <cylinderGeometry args={[0.45, 0.45, 3.2, 16]} />
        <meshStandardMaterial color={0xf0f0f0} metalness={0.9} roughness={0.1} />
      </mesh>

      {/* Nose cone */}
      <mesh position={[0, 2.1, 0]}>
        <coneGeometry args={[0.45, 1.2, 16]} />
        <meshStandardMaterial color={0xeaeaea} metalness={0.85} roughness={0.15} />
      </mesh>

      {/* Heat shield tiles */}
      <mesh position={[0, 0.5, -0.42]} rotation={[0.1, 0, 0]}>
        <boxGeometry args={[0.65, 2.6, 0.02]} />
        <meshStandardMaterial color={0x151515} roughness={0.95} />
      </mesh>

      {/* SpaceX logo */}
      <group position={[0.44, 0.6, 0]} rotation={[0, Math.PI / 2, 0]}>
        <mesh>
          <planeGeometry args={[0.7, 0.12]} />
          <meshStandardMaterial color={0x000000} roughness={0.8} />
        </mesh>
        {[-0.25, -0.15, -0.05, 0.05, 0.15].map((x, i) => (
          <mesh key={`r-${i}`} position={[x, 0.01, 0.001]}>
            <boxGeometry args={[0.06, 0.06, 0.002]} />
            <meshBasicMaterial color={0xffffff} />
          </mesh>
        ))}
      </group>

      {/* Flaps */}
      {[Math.PI/2, -Math.PI/2].map((rotY, i) => (
        <mesh key={`fwd-${i}`} position={[Math.cos(rotY) * 0.5, 1.7, Math.sin(rotY) * 0.5]} rotation={[0, rotY, 0.12]}>
          <boxGeometry args={[0.02, 0.45, 0.3]} />
          <meshStandardMaterial color={0x1a1a1a} roughness={0.85} />
        </mesh>
      ))}
      {[Math.PI/2, -Math.PI/2].map((rotY, i) => (
        <mesh key={`aft-${i}`} position={[Math.cos(rotY) * 0.5, -0.9, Math.sin(rotY) * 0.5]} rotation={[0, rotY, 0.1]}>
          <boxGeometry args={[0.02, 0.8, 0.45]} />
          <meshStandardMaterial color={0x1a1a1a} roughness={0.85} />
        </mesh>
      ))}

      {/* Engine section */}
      <mesh position={[0, -1.8, 0]}>
        <cylinderGeometry args={[0.4, 0.5, 0.35, 16]} />
        <meshStandardMaterial color={0x666666} metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Engine bells */}
      {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
        <mesh key={`eng-${i}`} position={[Math.cos(angle) * 0.18, -2.05, Math.sin(angle) * 0.18]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.1, 0.2, 12, 1, true]} />
          <meshStandardMaterial color={0x444444} metalness={0.9} roughness={0.2} side={THREE.DoubleSide} />
        </mesh>
      ))}

      {/* Exhaust plume */}
      <group ref={exhaustRef} position={[0, -2.3, 0]}>
        {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
          <group key={`exh-${i}`} position={[Math.cos(angle) * 0.18, 0, Math.sin(angle) * 0.18]}>
            <mesh>
              <coneGeometry args={[0.12, 1.0, 12]} />
              <meshBasicMaterial color={0xff6622} transparent opacity={0.7} blending={THREE.AdditiveBlending} />
            </mesh>
            <mesh position={[0, -0.2, 0]}>
              <coneGeometry args={[0.06, 0.6, 8]} />
              <meshBasicMaterial color={0xffffaa} transparent opacity={0.9} blending={THREE.AdditiveBlending} />
            </mesh>
          </group>
        ))}
        <mesh position={[0, -0.4, 0]}>
          <coneGeometry args={[0.25, 1.4, 12]} />
          <meshBasicMaterial color={0xff8844} transparent opacity={0.5} blending={THREE.AdditiveBlending} />
        </mesh>
      </group>

      {/* Engine light */}
      <pointLight ref={engineGlowRef} position={[0, -2.5, 0]} color={0xff8844} intensity={5} distance={20} />

      {/* Landing legs */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * Math.PI * 2) / 6;
        return (
          <group key={`leg-${i}`} position={[Math.cos(angle) * 0.4, -1.65, Math.sin(angle) * 0.4]} rotation={[0.45, angle, 0]}>
            <mesh position={[0, -0.35, 0]}>
              <boxGeometry args={[0.035, 0.75, 0.05]} />
              <meshStandardMaterial color={0x444444} metalness={0.85} roughness={0.25} />
            </mesh>
            <mesh position={[0, -0.75, 0]}>
              <cylinderGeometry args={[0.1, 0.12, 0.03, 8]} />
              <meshStandardMaterial color={0x333333} metalness={0.7} roughness={0.4} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// ============================================================
// JOURNEY STARSHIP - Follows the ENTIRE journey from Earth to Mercury
// ============================================================
// This is the PROTAGONIST - camera follows this ship throughout
// UPDATED TIMING: 0s to 72s (when HeroStarship takes over for landing)

// Journey waypoints - smooth path from Earth TO Mercury (same ship that lands!)
// Ship goes: Earth → transit → approaches Mercury → HeroStarship takes over for landing
// Mercury at (-2, 0, 15), polar base at (-1.64, 1.764, 15.27)
// HeroStarship _approachStart at (15, 25, 30)
// SCALED to match shot timing: handoff at 72s (start of STARSHIP LANDING)
const JOURNEY_WAYPOINTS = [
  { t: 0,    pos: new THREE.Vector3(50, 1.5, 0.3) },    // Earth surface - LIFTOFF
  { t: 6,    pos: new THREE.Vector3(50, 8, 3) },        // Climbing
  { t: 12,   pos: new THREE.Vector3(48, 15, 8) },       // Leaving Earth orbit
  { t: 19,   pos: new THREE.Vector3(40, 12, 11) },      // Transit begins
  { t: 28,   pos: new THREE.Vector3(28, 10, 13) },      // Mid transit - camera shows mining ships
  { t: 38,   pos: new THREE.Vector3(15, 8, 14) },       // Approaching Mercury region
  { t: 50,   pos: new THREE.Vector3(5, 7, 15) },        // Mercury visible ahead
  { t: 62,   pos: new THREE.Vector3(0, 10, 18) },       // Near Mercury - laser focus begins
  { t: 67,   pos: new THREE.Vector3(8, 18, 25) },       // Positioning for descent
  { t: 72,   pos: new THREE.Vector3(15, 25, 30) },      // HANDOFF at 72s - exact HeroStarship _approachStart!
];

// Pre-allocated vectors
const _journeyPos = new THREE.Vector3();
const _journeyDir = new THREE.Vector3();
const _journeyPrevPos = new THREE.Vector3();
const _journeyQuat = new THREE.Quaternion();
const _journeyUp = new THREE.Vector3(0, 1, 0);

// Catmull-Rom spline interpolation for smooth curves
function catmullRom(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, t: number, out: THREE.Vector3) {
  const t2 = t * t;
  const t3 = t2 * t;

  out.x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
  out.y = 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
  out.z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3);
}

// Get position along journey at given time
function getJourneyPosition(time: number, out: THREE.Vector3) {
  // Find the segment we're in
  let i = 0;
  for (let j = 0; j < JOURNEY_WAYPOINTS.length - 1; j++) {
    if (time >= JOURNEY_WAYPOINTS[j].t && time < JOURNEY_WAYPOINTS[j + 1].t) {
      i = j;
      break;
    }
  }
  if (time >= JOURNEY_WAYPOINTS[JOURNEY_WAYPOINTS.length - 1].t) {
    i = JOURNEY_WAYPOINTS.length - 2;
  }

  // Get segment progress
  const segStart = JOURNEY_WAYPOINTS[i].t;
  const segEnd = JOURNEY_WAYPOINTS[i + 1].t;
  const segT = Math.max(0, Math.min(1, (time - segStart) / (segEnd - segStart)));

  // Get 4 points for catmull-rom (clamped at edges)
  const p0 = JOURNEY_WAYPOINTS[Math.max(0, i - 1)].pos;
  const p1 = JOURNEY_WAYPOINTS[i].pos;
  const p2 = JOURNEY_WAYPOINTS[i + 1].pos;
  const p3 = JOURNEY_WAYPOINTS[Math.min(JOURNEY_WAYPOINTS.length - 1, i + 2)].pos;

  catmullRom(p0, p1, p2, p3, segT, out);
}

// Export for camera tracking
export function getJourneyShipPosition(time: number): THREE.Vector3 {
  const pos = new THREE.Vector3();
  getJourneyPosition(time, pos);
  return pos;
}

export function LaunchStarship() {
  const groupRef = useRef<THREE.Group>(null);
  const exhaustRef = useRef<THREE.Group>(null);
  const trailRef = useRef<THREE.Points>(null);
  const innerExhaustRef = useRef<THREE.Mesh>(null);
  const initializedRef = useRef(false);

  // Trail particles
  const trailPositions = useMemo(() => {
    const positions = new Float32Array(200 * 3);
    return positions;
  }, []);

  useFrame((state) => {
    if (!groupRef.current) return;

    const { time, active } = getClipTime();

    // Visible during ENTIRE journey (0s to 72s when HeroStarship takes over)
    if (!active || time > HERO_SHIP_START) {
      groupRef.current.visible = false;
      if (trailRef.current) trailRef.current.visible = false;
      initializedRef.current = false; // Reset for next clip
      return;
    }

    groupRef.current.visible = true;
    if (trailRef.current) trailRef.current.visible = true;

    // Get current position on journey
    getJourneyPosition(time, _journeyPos);

    // INITIALIZE ORIENTATION - rocket starts pointing UP (vertical liftoff)
    if (!initializedRef.current || time < 0.1) {
      groupRef.current.position.copy(_journeyPos);
      groupRef.current.quaternion.setFromEuler(new THREE.Euler(0, 0, 0)); // Point up
      _journeyPrevPos.copy(_journeyPos);
      initializedRef.current = true;
      return;
    }

    // Store previous position for direction
    _journeyPrevPos.copy(groupRef.current.position);
    groupRef.current.position.copy(_journeyPos);

    // Engine intensity based on journey phase
    let engineIntensity: number;
    let engineColor = 0xff6622; // Orange default

    if (time < 17) {
      // LIFTOFF & CLIMB - max thrust
      engineIntensity = 3.0 + Math.sin(time * 20) * 0.5;
      engineColor = 0xff4400; // Bright orange
    } else if (time < 36) {
      // TRANSIT BURN - sustained
      engineIntensity = 2.0 + Math.sin(time * 12) * 0.3;
      engineColor = 0xff6622;
    } else if (time < 70) {
      // CRUISE - lighter
      engineIntensity = 1.0 + Math.sin(time * 8) * 0.2;
      engineColor = 0x88aaff; // Blueish ion
    } else {
      // APPROACH - braking burns
      engineIntensity = 1.5 + Math.sin(time * 15) * 0.3;
      engineColor = 0xff8844;
    }

    // Point in direction of travel - smooth rotation
    _journeyDir.subVectors(_journeyPos, _journeyPrevPos);
    if (_journeyDir.lengthSq() > 0.00001) {
      _journeyDir.normalize();
      _journeyQuat.setFromUnitVectors(_journeyUp, _journeyDir);
      // Slower rotation at start for smooth liftoff, faster later
      const rotSpeed = time < 5 ? 0.03 : 0.08;
      groupRef.current.quaternion.slerp(_journeyQuat, rotSpeed);
    }

    // Update exhaust
    if (exhaustRef.current) {
      const scale = 0.5 + engineIntensity * 0.3;
      exhaustRef.current.scale.set(scale, scale * 1.5, scale);

      // Update exhaust color
      exhaustRef.current.children.forEach((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
          child.material.color.setHex(engineColor);
        }
        if (child instanceof THREE.PointLight) {
          child.color.setHex(engineColor);
          child.intensity = engineIntensity * 3;
        }
      });
    }

    // Inner exhaust glow
    if (innerExhaustRef.current) {
      const mat = innerExhaustRef.current.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.6 + Math.sin(time * 30) * 0.3;
    }

    // Update trail
    if (trailRef.current) {
      const positions = trailRef.current.geometry.attributes.position.array as Float32Array;
      for (let i = positions.length - 3; i >= 3; i -= 3) {
        positions[i] = positions[i - 3];
        positions[i + 1] = positions[i - 2];
        positions[i + 2] = positions[i - 1];
      }
      positions[0] = _journeyPos.x;
      positions[1] = _journeyPos.y - 0.15;
      positions[2] = _journeyPos.z;
      trailRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <>
      <group ref={groupRef} visible={false} scale={0.2}>
        {/* Starship body - stainless steel */}
        <mesh>
          <cylinderGeometry args={[0.4, 0.4, 3, 16]} />
          <meshStandardMaterial color={0xf0f0f0} metalness={0.9} roughness={0.1} />
        </mesh>

        {/* Nose cone */}
        <mesh position={[0, 1.8, 0]}>
          <coneGeometry args={[0.4, 0.9, 16]} />
          <meshStandardMaterial color={0xeaeaea} metalness={0.85} roughness={0.15} />
        </mesh>

        {/* Heat shield side */}
        <mesh position={[0, 0.3, -0.38]} rotation={[0.08, 0, 0]}>
          <boxGeometry args={[0.55, 2.4, 0.02]} />
          <meshStandardMaterial color={0x151515} roughness={0.95} />
        </mesh>

        {/* SpaceX logo */}
        <group position={[0.39, 0.5, 0]} rotation={[0, Math.PI / 2, 0]}>
          <mesh>
            <planeGeometry args={[0.6, 0.1]} />
            <meshStandardMaterial color={0x000000} />
          </mesh>
          {[-0.2, -0.12, -0.04, 0.04, 0.12].map((x, i) => (
            <mesh key={i} position={[x, 0, 0.001]}>
              <boxGeometry args={[0.05, 0.05, 0.002]} />
              <meshBasicMaterial color={0xffffff} />
            </mesh>
          ))}
        </group>

        {/* Flaps */}
        {[1, -1].map((side, i) => (
          <mesh key={i} position={[side * 0.45, 1.5, 0]} rotation={[0, 0, side * 0.1]}>
            <boxGeometry args={[0.02, 0.4, 0.25]} />
            <meshStandardMaterial color={0x1a1a1a} />
          </mesh>
        ))}
        {[1, -1].map((side, i) => (
          <mesh key={`aft-${i}`} position={[side * 0.45, -0.8, 0]} rotation={[0, 0, side * 0.08]}>
            <boxGeometry args={[0.02, 0.7, 0.4]} />
            <meshStandardMaterial color={0x1a1a1a} />
          </mesh>
        ))}

        {/* Engine section */}
        <mesh position={[0, -1.7, 0]}>
          <cylinderGeometry args={[0.35, 0.45, 0.3, 16]} />
          <meshStandardMaterial color={0x555555} metalness={0.8} roughness={0.3} />
        </mesh>

        {/* Engine bells */}
        {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
          <mesh key={i} position={[Math.cos(angle) * 0.15, -1.95, Math.sin(angle) * 0.15]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.08, 0.18, 10, 1, true]} />
            <meshStandardMaterial color={0x444444} metalness={0.9} roughness={0.2} side={THREE.DoubleSide} />
          </mesh>
        ))}

        {/* EXHAUST PLUME - Very visible */}
        <group ref={exhaustRef} position={[0, -2.2, 0]}>
          {/* Outer flame */}
          <mesh>
            <coneGeometry args={[0.3, 1.8, 12]} />
            <meshBasicMaterial
              color={0xff6622}
              transparent
              opacity={0.85}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          {/* Inner hot core */}
          <mesh ref={innerExhaustRef} position={[0, -0.2, 0]}>
            <coneGeometry args={[0.15, 1.2, 8]} />
            <meshBasicMaterial
              color={0xffffaa}
              transparent
              opacity={0.9}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          {/* Light source */}
          <pointLight color={0xff8844} intensity={10} distance={20} />
        </group>
      </group>

      {/* Exhaust trail */}
      <points ref={trailRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={200}
            array={trailPositions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.12}
          color={0xff8844}
          transparent
          opacity={0.5}
          blending={THREE.AdditiveBlending}
          sizeAttenuation
        />
      </points>
    </>
  );
}

// ============================================================
// COCKPIT INTERIOR - Photos of Elon & Ham visible during COCKPIT scene
// ============================================================
// Visible during COCKPIT INTERIOR shot (17-21s)
const COCKPIT_START = 17;
const COCKPIT_END = 21;

export function CockpitInterior() {
  const groupRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (!groupRef.current) return;

    const { time, active } = getClipTime();

    // Only visible during cockpit shot
    if (!active || time < COCKPIT_START || time > COCKPIT_END) {
      groupRef.current.visible = false;
      return;
    }

    groupRef.current.visible = true;

    // Position cockpit interior relative to ship position at this time
    // Ship is around (40, 12, 11.5) during this shot
    const shipPos = getJourneyPositionAt(time);
    groupRef.current.position.copy(shipPos);

    // Get ship direction for orientation
    const prevPos = getJourneyPositionAt(time - 0.1);
    const dir = new THREE.Vector3().subVectors(shipPos, prevPos).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    groupRef.current.quaternion.copy(quat);
  });

  return (
    <group ref={groupRef} visible={false}>
      {/* Cockpit frame - dark interior */}
      <mesh position={[0, 0.3, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 0.8, 8, 1, true]} />
        <meshStandardMaterial color={0x1a1a1a} side={THREE.BackSide} />
      </mesh>

      {/* Dashboard panel */}
      <mesh position={[0, 0.1, 0.25]} rotation={[-0.3, 0, 0]}>
        <boxGeometry args={[0.5, 0.2, 0.02]} />
        <meshStandardMaterial color={0x2a2a2a} metalness={0.5} roughness={0.7} />
      </mesh>

      {/* ELON MUSK PHOTO - Left side of dashboard */}
      <group position={[-0.15, 0.15, 0.24]} rotation={[-0.3, 0, 0]}>
        {/* Photo frame */}
        <mesh>
          <boxGeometry args={[0.08, 0.1, 0.005]} />
          <meshStandardMaterial color={0x333333} />
        </mesh>
        {/* Photo "image" - simplified representation */}
        <mesh position={[0, 0, 0.003]}>
          <planeGeometry args={[0.07, 0.09]} />
          <meshBasicMaterial color={0xddccbb} /> {/* Skin tone placeholder */}
        </mesh>
        {/* "ELON" label */}
        <mesh position={[0, -0.06, 0.003]}>
          <planeGeometry args={[0.06, 0.015]} />
          <meshBasicMaterial color={0xffffff} />
        </mesh>
      </group>

      {/* HAM THE CHIMP PHOTO - Right side of dashboard */}
      <group position={[0.15, 0.15, 0.24]} rotation={[-0.3, 0, 0]}>
        {/* Photo frame */}
        <mesh>
          <boxGeometry args={[0.08, 0.1, 0.005]} />
          <meshStandardMaterial color={0x333333} />
        </mesh>
        {/* Photo "image" - Ham in spacesuit (simplified) */}
        <mesh position={[0, 0, 0.003]}>
          <planeGeometry args={[0.07, 0.09]} />
          <meshBasicMaterial color={0x8b7355} /> {/* Brown for chimp */}
        </mesh>
        {/* Space helmet detail */}
        <mesh position={[0, 0.02, 0.004]}>
          <ringGeometry args={[0.025, 0.03, 16]} />
          <meshBasicMaterial color={0xcccccc} />
        </mesh>
        {/* "HAM" label */}
        <mesh position={[0, -0.06, 0.003]}>
          <planeGeometry args={[0.05, 0.015]} />
          <meshBasicMaterial color={0xffffff} />
        </mesh>
      </group>

      {/* Control screens (ambient light) */}
      <mesh position={[0, 0.05, 0.22]} rotation={[-0.2, 0, 0]}>
        <planeGeometry args={[0.3, 0.08]} />
        <meshBasicMaterial color={0x003366} />
      </mesh>

      {/* Window frame */}
      <mesh position={[0, 0.5, 0]}>
        <ringGeometry args={[0.25, 0.3, 6]} />
        <meshStandardMaterial color={0x2a2a2a} />
      </mesh>

      {/* Ambient cockpit light */}
      <pointLight position={[0, 0.2, 0.1]} color={0x88aaff} intensity={0.5} distance={1} />
    </group>
  );
}

// Overlay component for React DOM (outside Canvas)
// Pure cinematic experience - letterbox bars, cockpit overlay for POV shots
export function MusicVideoOverlay() {
  const { isClipMode, stopClipMode, clipShotName } = useStore();
  const [displayTime, setDisplayTime] = useState(0);
  const [shotInfo, setShotInfo] = useState({ shotIndex: 0, totalShots: 0 });

  // Update time display every 100ms
  useEffect(() => {
    if (!isClipMode) return;

    const interval = setInterval(() => {
      const { time } = getClipTime();
      const info = getClipShotInfo();
      setDisplayTime(time);
      setShotInfo(info);
    }, 100);

    return () => clearInterval(interval);
  }, [isClipMode]);

  // Handle ESC key to exit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isClipMode) {
        stopClipMode();
      }
    };

    if (isClipMode) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isClipMode, stopClipMode]);

  if (!isClipMode) return null;

  // Check shot type for overlays
  const shotName = clipShotName || '';
  const isPOVShot = shotName.includes('POV') || shotName.includes('CABIN') || shotName.includes('WINDOW');
  const isEngineShotBelow = shotName.includes('MOON FOREGROUND') || shotName.includes('ROCKET RISING');
  const isEarthLanding = shotName.includes('EARTH LANDING') || shotName.includes('EARTH ENTRY') || shotName.includes('RETURN DEPARTURE');

  // Format time as MM:SS.d
  const minutes = Math.floor(displayTime / 60);
  const seconds = displayTime % 60;

  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {/* Cinematic letterbox bars - pure cinema feel */}
      <div className="absolute top-0 left-0 right-0 h-[8%] bg-black" />
      <div className="absolute bottom-0 left-0 right-0 h-[8%] bg-black" />

      {/* TIME COUNTER - Debug overlay for precise timing feedback */}
      <div className="absolute bottom-[1%] left-4 font-mono text-white/90 z-[70]">
        <div className="bg-black/70 px-3 py-1.5 rounded border border-white/20 flex items-center gap-3">
          <div className="text-xl font-bold tracking-wider">
            {minutes}:{seconds.toFixed(1).padStart(4, '0')}
          </div>
          <div className="text-xs text-gray-400 border-l border-white/20 pl-3">
            CENA {shotInfo.shotIndex + 1}/{shotInfo.totalShots}
          </div>
        </div>
      </div>

      {/* ENGINE FIRE EFFECT - for shots from below the rocket */}
      {isEngineShotBelow && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Main engine glow - bottom right corner (rocket is in that area) */}
          <div
            className="absolute w-[300px] h-[400px] animate-pulse"
            style={{
              right: '20%',
              bottom: '15%',
              background: 'radial-gradient(ellipse at center, rgba(255,100,0,0.4) 0%, rgba(255,60,0,0.2) 30%, rgba(255,30,0,0.1) 50%, transparent 70%)',
              filter: 'blur(20px)',
              animation: 'pulse 0.1s ease-in-out infinite alternate'
            }}
          />
          {/* Inner hot core */}
          <div
            className="absolute w-[150px] h-[250px]"
            style={{
              right: '25%',
              bottom: '18%',
              background: 'radial-gradient(ellipse at center, rgba(255,220,150,0.5) 0%, rgba(255,150,50,0.3) 40%, transparent 70%)',
              filter: 'blur(15px)',
              animation: 'pulse 0.08s ease-in-out infinite alternate'
            }}
          />
          {/* Flame streaks */}
          <div
            className="absolute w-[80px] h-[350px]"
            style={{
              right: '28%',
              bottom: '10%',
              background: 'linear-gradient(to top, transparent 0%, rgba(255,100,0,0.3) 20%, rgba(255,200,100,0.4) 40%, rgba(255,100,0,0.2) 70%, transparent 100%)',
              filter: 'blur(10px)',
              transform: 'rotate(-5deg)'
            }}
          />
          {/* Secondary flame */}
          <div
            className="absolute w-[60px] h-[280px]"
            style={{
              right: '32%',
              bottom: '12%',
              background: 'linear-gradient(to top, transparent 0%, rgba(255,80,0,0.25) 30%, rgba(255,150,50,0.3) 50%, transparent 100%)',
              filter: 'blur(8px)',
              transform: 'rotate(3deg)'
            }}
          />
        </div>
      )}

      {/* EARTH LANDING ENGINE FIRE - centered dramatic effect */}
      {isEarthLanding && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Central engine glow - ship centered in frame */}
          <div
            className="absolute w-[400px] h-[500px]"
            style={{
              left: '50%',
              bottom: '10%',
              transform: 'translateX(-50%)',
              background: 'radial-gradient(ellipse at center, rgba(255,80,0,0.5) 0%, rgba(255,50,0,0.3) 30%, rgba(255,30,0,0.15) 50%, transparent 70%)',
              filter: 'blur(25px)',
              animation: 'pulse 0.12s ease-in-out infinite alternate'
            }}
          />
          {/* Inner hot core - white/yellow center */}
          <div
            className="absolute w-[200px] h-[300px]"
            style={{
              left: '50%',
              bottom: '15%',
              transform: 'translateX(-50%)',
              background: 'radial-gradient(ellipse at center, rgba(255,240,200,0.6) 0%, rgba(255,180,80,0.4) 40%, transparent 70%)',
              filter: 'blur(15px)',
              animation: 'pulse 0.08s ease-in-out infinite alternate'
            }}
          />
          {/* Retro burn streaks */}
          <div
            className="absolute w-[120px] h-[450px]"
            style={{
              left: '48%',
              bottom: '5%',
              background: 'linear-gradient(to top, transparent 0%, rgba(255,100,0,0.35) 20%, rgba(255,200,100,0.45) 40%, rgba(255,100,0,0.25) 70%, transparent 100%)',
              filter: 'blur(12px)',
              transform: 'rotate(-2deg)'
            }}
          />
          <div
            className="absolute w-[100px] h-[400px]"
            style={{
              left: '52%',
              bottom: '8%',
              background: 'linear-gradient(to top, transparent 0%, rgba(255,80,0,0.3) 30%, rgba(255,150,50,0.35) 50%, transparent 100%)',
              filter: 'blur(10px)',
              transform: 'rotate(2deg)'
            }}
          />
        </div>
      )}

      {/* REALISTIC COCKPIT OVERLAY - First person view through window */}
      {isPOVShot && (
        <div className="absolute inset-0 pointer-events-none">
          {/* === THICK WINDOW FRAME (like real spacecraft) === */}
          {/* Top frame - thick metal with rivets */}
          <div className="absolute top-[8%] left-0 right-0 h-[15%] bg-gradient-to-b from-gray-900 via-[#1a1a1a] to-[#0d0d0d]">
            {/* Metal texture lines */}
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)'
            }} />
            {/* Rivets row */}
            <div className="absolute bottom-3 left-[10%] right-[10%] flex justify-between">
              {Array.from({length: 12}).map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-gray-600 shadow-inner" style={{boxShadow: 'inset 1px 1px 2px rgba(0,0,0,0.8)'}} />
              ))}
            </div>
          </div>

          {/* Left frame - thick with control panel */}
          <div className="absolute top-[23%] left-0 w-[18%] h-[54%] bg-gradient-to-r from-gray-900 via-[#1a1a1a] to-[#0a0a0a]">
            {/* Side control panel */}
            <div className="absolute top-[20%] right-2 w-[80%] h-[60%] bg-[#0a0a0a] rounded border border-gray-700">
              {/* Switches row */}
              <div className="p-2 space-y-3">
                {['RCS', 'NAV', 'COM', 'PWR'].map((label, i) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="text-[8px] text-gray-500 w-6">{label}</div>
                    <div className={`w-3 h-5 rounded-sm ${i % 2 === 0 ? 'bg-gray-600' : 'bg-gray-700'}`}
                         style={{boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.5)'}} />
                    <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-green-500' : i === 2 ? 'bg-yellow-500' : 'bg-green-400'}`}
                         style={{boxShadow: '0 0 4px currentColor'}} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right frame - thick with status panel */}
          <div className="absolute top-[23%] right-0 w-[18%] h-[54%] bg-gradient-to-l from-gray-900 via-[#1a1a1a] to-[#0a0a0a]">
            {/* Status panel */}
            <div className="absolute top-[20%] left-2 w-[80%] h-[60%] bg-[#0a0a0a] rounded border border-gray-700">
              <div className="p-2 space-y-2">
                <div className="text-[7px] text-gray-400 border-b border-gray-700 pb-1">SYSTEMS</div>
                {['LIFE SUP', 'THERMAL', 'STRUCT', 'FUEL'].map((sys, i) => (
                  <div key={sys} className="flex items-center justify-between">
                    <span className="text-[7px] text-gray-500">{sys}</span>
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{boxShadow: '0 0 3px #22c55e'}} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom frame - MAIN CONTROL CONSOLE */}
          <div className="absolute bottom-[8%] left-0 right-0 h-[23%] bg-gradient-to-t from-gray-900 via-[#1a1a1a] to-[#0d0d0d]">
            {/* Main console surface */}
            <div className="absolute top-0 left-[15%] right-[15%] h-full">
              {/* Central display */}
              <div className="absolute top-2 left-[30%] right-[30%] h-[45%] bg-[#0a0a12] rounded border border-gray-600" style={{boxShadow: 'inset 0 0 20px rgba(0,100,255,0.1)'}}>
                <div className="p-2 h-full flex flex-col justify-between">
                  <div className="text-[8px] text-cyan-400 font-mono">NAVIGATION</div>
                  <div className="flex justify-between text-[10px] font-mono">
                    <div>
                      <div className="text-gray-500">HDG</div>
                      <div className="text-green-400">247.3°</div>
                    </div>
                    <div>
                      <div className="text-gray-500">DIST</div>
                      <div className="text-green-400">0.38 AU</div>
                    </div>
                    <div>
                      <div className="text-gray-500">ETA</div>
                      <div className="text-cyan-400">14:32:07</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Left instrument cluster */}
              <div className="absolute top-2 left-2 w-[25%] h-[50%]">
                <div className="bg-[#0a0a0a] rounded p-1 border border-gray-700 h-full">
                  <div className="text-[7px] text-gray-500 mb-1">VELOCITY</div>
                  <div className="text-lg font-mono text-green-400" style={{textShadow: '0 0 5px rgba(74,222,128,0.5)'}}>7,842</div>
                  <div className="text-[8px] text-gray-500">m/s</div>
                </div>
              </div>

              {/* Right instrument cluster */}
              <div className="absolute top-2 right-2 w-[25%] h-[50%]">
                <div className="bg-[#0a0a0a] rounded p-1 border border-gray-700 h-full">
                  <div className="text-[7px] text-gray-500 mb-1">ALTITUDE</div>
                  <div className="text-lg font-mono text-cyan-400" style={{textShadow: '0 0 5px rgba(34,211,238,0.5)'}}>384</div>
                  <div className="text-[8px] text-gray-500">km</div>
                </div>
              </div>

              {/* Physical controls row */}
              <div className="absolute bottom-2 left-4 right-4 flex justify-between items-end">
                {/* Throttle */}
                <div className="flex flex-col items-center">
                  <div className="w-4 h-16 bg-gray-800 rounded relative border border-gray-600">
                    <div className="absolute bottom-1 left-1 right-1 h-[70%] bg-gradient-to-t from-blue-600 to-blue-400 rounded-sm" />
                  </div>
                  <div className="text-[6px] text-gray-500 mt-1">THR</div>
                </div>

                {/* Center buttons */}
                <div className="flex gap-3">
                  {['ARM', 'IGN', 'SEP'].map((btn, i) => (
                    <div key={btn} className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full border-2 ${i === 1 ? 'border-red-600 bg-red-900' : 'border-gray-600 bg-gray-800'}`}
                           style={{boxShadow: i === 1 ? '0 0 8px rgba(220,38,38,0.5)' : 'none'}} />
                      <div className="text-[6px] text-gray-500 mt-1">{btn}</div>
                    </div>
                  ))}
                </div>

                {/* Joystick base */}
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                    <div className="w-3 h-8 bg-gray-500 rounded-full" style={{transform: 'rotate(-5deg)'}} />
                  </div>
                  <div className="text-[6px] text-gray-500 mt-1">RCS</div>
                </div>
              </div>
            </div>

            {/* Falcon 9 photo - pilot tradition */}
            <div className="absolute bottom-4 left-4 w-[60px] h-[75px] border-2 border-gray-500 rounded bg-gray-800 overflow-hidden">
              <div className="w-full h-full bg-gradient-to-b from-orange-500 via-orange-300 to-blue-200 flex items-center justify-center">
                <div className="w-[4px] h-[40px] bg-black relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-b-[10px] border-transparent border-b-black" />
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3 h-4 bg-orange-500 rounded-b-full" style={{filter: 'blur(1px)'}} />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[5px] text-white text-center py-0.5">SPACEX F9</div>
            </div>
          </div>

          {/* Window glass effect - subtle reflection */}
          <div className="absolute top-[23%] left-[18%] right-[18%] bottom-[31%] pointer-events-none rounded-lg overflow-hidden">
            {/* Glass reflection streak */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.02) 100%)'
            }} />
            {/* Window frame edge glow */}
            <div className="absolute inset-0" style={{
              boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5), inset 0 0 60px rgba(0,0,0,0.3)'
            }} />
          </div>
        </div>
      )}

      {/* Subtle vignette overlay for depth */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)'
        }}
      />

      {/* Invisible click area to exit - click anywhere to stop */}
      <div
        className="absolute inset-0 pointer-events-auto cursor-pointer opacity-0"
        onClick={stopClipMode}
        title="Click or press ESC to exit"
      />
    </div>
  );
}
